import { ChangeEvent, useEffect, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import { InputAdornment, TextField } from '@mui/material'
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers'
import { format, isValid, parse } from 'date-fns'
import { inRange, isNaN } from 'lodash'

import { DefaultElementProps } from 'common/interfaces'
import { textFieldPickerStyle } from 'common/styles'
import InputLabel from 'components/InputLabel'
import { SizingContainer } from 'components/SizingContainer'
import { useIsPhone, useTemplate } from 'utils/hook'
import useSetValueAsTZFormat from 'utils/useSetValueAsTZFormat'
import validationsToRules from 'utils/validationsToRules'

import { TimePicker as TimePickerProps } from './interface'

function TimePickerTextField({ ...props }): JSX.Element {
  return (
    <TextField
      {...props}
      sx={textFieldPickerStyle}
      fullWidth
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <AccessTimeIcon />
          </InputAdornment>
        ),
      }}
    />
  )
}

export default function TimePicker({
  name,
  size,
  label,
  onFocus,
  onChange,
  validations,
  defaultValue = '',
}: TimePickerProps & DefaultElementProps): JSX.Element {
  const isPhone = useIsPhone()
  const render = useTemplate()

  const renderedDefaultValue = render(
    isPhone && defaultValue !== ''
      ? new Date(defaultValue).toLocaleTimeString('en-US', { hour12: false })
      : defaultValue
  )

  const validationRules = {
    ...validationsToRules(validations),
    validate: {
      ...validationsToRules(validations)?.validate,
      validTime: (selectedTime: Date) => {
        if (
          (validations.required && (!selectedTime || isNaN(selectedTime.valueOf()))) ??
          (!validations.required && selectedTime && isNaN(selectedTime.valueOf()))
        )
          return 'Invalid value'
        return true
      },
    },
  }

  const { control, setValue, clearErrors } = useFormContext()
  const [mobileValue, setMobileValue] = useState('')
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: { ...validationRules },
    defaultValue: renderedDefaultValue,
  })

  useEffect(() => {
    const dateValue = new Date(field.value)
    if (field.value && !isNaN(dateValue.getTime())) {
      setMobileValue(format(dateValue, 'HH:mm'))
    }
  }, [])

  const onChangeTimePicker = (newValue: Date | null): void => {
    clearErrors(name)
    if (newValue && !isNaN(newValue.valueOf())) {
      setValue(name, newValue ? new Date(newValue).toISOString() : newValue)

      return
    }

    setValue(name, newValue ?? '')
  }

  const handleChangeMobile = (e: ChangeEvent<HTMLInputElement>): void => {
    clearErrors(name)
    const newValue = e.target.value
    setValue(name, newValue)
    setMobileValue(newValue)

    const parsedTime = parse(newValue, 'HH:mm', new Date())

    if (isValid(parsedTime)) {
      const currentDate = new Date()
      const datetimeWithTime = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
        parsedTime.getHours(),
        parsedTime.getMinutes()
      )

      setValue(name, datetimeWithTime.toISOString())
    }
  }

  const muiTimePicker = (
    <MuiTimePicker
      {...field}
      disableOpenPicker
      label={<InputLabel label={label} />}
      onChange={onChangeTimePicker}
      renderInput={(params) => {
        const isInvalidDate = params?.error === true && field.value instanceof Date
        const helperText = inRange(params?.inputProps?.value.length, 6, 8)
          ? 'Add AM/PM format, please'
          : isInvalidDate
          ? 'Enter correct format, please'
          : error?.message ?? ''

        return (
          <TimePickerTextField
            {...params}
            required={validations.required?.value}
            helperText={helperText}
            onFocus={onFocus}
            error={isInvalidDate || error !== undefined}
          />
        )
      }}
    />
  )

  const nativeTimePicker = (
    <TimePickerTextField
      {...field}
      value={mobileValue}
      required={validations.required?.value}
      type="time"
      label={label}
      error={error !== undefined}
      helperText={error?.message}
      onFocus={onFocus}
      onChange={handleChangeMobile}
    />
  )

  useSetValueAsTZFormat(field)

  return (
    <SizingContainer className="time-picker-wrap" size={size}>
      {isPhone ? nativeTimePicker : muiTimePicker}
    </SizingContainer>
  )
}
